/**
 * What a kitchen designer would say, before the maker has to.
 *
 * `layout.js` guarantees the arithmetic: every run is exactly full, every width is one the
 * maker will cut. That is necessary and it is not sufficient. A run can be arithmetically
 * perfect and still put the cooktop hard against the fridge, the dishwasher two metres from
 * the sink, and a plain cupboard where the rangehood has to go -- and a homeowner designing
 * unattended has no way of knowing, so the maker finds out on the phone.
 *
 * These are the rules a designer applies in the first five minutes. Sourced, in millimetres,
 * from the NKBA Kitchen Planning Guidelines (the industry's reference, converted from inches),
 * the Australian guidance that sits over them (AS 4386, AS/NZS 5601 for gas, and what every
 * local maker actually does), and Caesarstone's fabrication rules for stone.
 *
 * Two severities, and the difference is the whole point:
 *   fault   -- the maker will not build it as drawn, so sign-off waits
 *   advice  -- a designer would raise it; the homeowner decides
 *
 * Pure functions on the plan. No three.js, no Vue.
 */
import { offsets } from './layout.js'
import * as room from './room.js'

/** The numbers. Named, so the reason for each is one place. */
export const RULE = {
  cooktopSide: 300,        // bench either side of a cooktop (AU practice; NKBA 305/380)
  cooktopFromWall: 300,    // and it cannot sit in the corner or against a side wall
  sinkSideMajor: 600,      // NKBA 24" one side of the sink ...
  sinkSideMinor: 450,      // ... 18" the other
  dishwasherToSink: 900,   // NKBA: nearest edge within 36" of the sink
  fridgeLanding: 380,      // NKBA 15" on the handle side
  hoodElectric: 600,       // cooktop to rangehood, electric / induction
  hoodGas: 650,            // AS/NZS 5601
  slab: 3050,              // a stone slab; longer than this and there is a join
  overhangMax: 300,        // unsupported stone beyond this cracks
  triangleLeg: [1200, 2700], // NKBA 4'–9' per leg
  triangleTotal: 7900,     // NKBA 26'
  walkway: 1000,           // between facing runs, one cook (AU); 1200 for two
  walkwayTwo: 1200,
}

/**
 * Run every rule over a plan.
 *
 * Returns findings, each `{ level, code, text, runId?, itemId? }`, worst first. Text is
 * written for the homeowner: what is wrong, and what to do about it, in a sentence.
 */
export function review(plan, maker) {
  const out = []
  const ctx = context(plan, maker)

  cooktop(ctx, out)
  sink(ctx, out)
  dishwasher(ctx, out)
  fridge(ctx, out)
  corners(ctx, out)
  overheadsAboveCooktop(ctx, out)
  stone(ctx, out)
  triangle(ctx, out)
  walkways(ctx, out)
  openings(ctx, out)

  const rank = { fault: 0, advice: 1 }
  return out.sort((a, b) => rank[a.level] - rank[b.level])
}

/** Everything the rules need, computed once: each placed cabinet with its spec and bounds. */
function context(plan, maker) {
  const specs = maker.cabinets
  const lim = maker.limits
  const placed = []
  for (const run of plan.runs) {
    const at = offsets(run.items)
    run.items.forEach((item, i) => {
      const spec = specs.find((s) => s.id === item.specId)
      const start = run.from + at[i]
      placed.push({
        run, item, spec, index: i,
        start, end: start + item.w,
        kind: run.kind,
        role: item.filler ? 'filler' : (spec?.role ?? null),
        // in room coordinates, for anything that has to cross runs
        xy: roomXY(run, start + item.w / 2, plan, lim),
      })
    })
  }
  return { plan, maker, lim, placed, runs: plan.runs }
}

/** Where a point along a run sits on the floor, so distances between runs mean something. */
function roomXY(run, along, plan, lim) {
  // Through room.segment, so a run on an island is a real point on the floor. The old
  // lookup missed islands and answered `{ x: along, y: 0 }`, which put the sink at the
  // origin and made every work-triangle distance a fiction.
  const w = room.segment(plan, run.wallId)
  if (!w) return { x: along, y: 0 }
  const depth = run.kind === 'wall' ? lim.wallDepth : lim.baseDepth
  const p = room.at(w, along)
  const n = room.inward(w)
  return { x: p.x + n.x * depth / 2, y: p.y + n.y * depth / 2 }
}

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

/** Bench frontage beside a cabinet, in a direction, until something that is not bench. */
function benchBeside(ctx, p, dir) {
  const row = ctx.placed.filter((q) => q.run === p.run).sort((a, b) => a.start - b.start)
  let i = row.indexOf(p) + dir
  let bench = 0
  while (i >= 0 && i < row.length) {
    const q = row[i]
    // A base cabinet with a benchtop over it is landing space. A tall unit, a fridge, or an
    // appliance cut into the bench is not.
    if (q.kind !== 'base' || ['fridge', 'oven', 'cooktop', 'sink'].includes(q.role)) break
    if (q.spec?.h > 900) break
    bench += q.item.w
    i += dir
  }
  return bench
}

function wallBeside(ctx, p, dir) {
  const row = ctx.placed.filter((q) => q.run === p.run).sort((a, b) => a.start - b.start)
  const i = row.indexOf(p) + dir
  return i < 0 || i >= row.length
}

// ── the rules ────────────────────────────────────────────────────────────────────────

function cooktop(ctx, out) {
  for (const p of ctx.placed.filter((q) => q.role === 'cooktop')) {
    const left = benchBeside(ctx, p, -1)
    const right = benchBeside(ctx, p, 1)
    const short = Math.min(left, right)
    if (wallBeside(ctx, p, -1) || wallBeside(ctx, p, 1)) {
      out.push({ level: 'fault', code: 'cooktop-wall', runId: p.run.id, itemId: p.item.id,
        text: 'The cooktop is hard against the wall. It needs ' + RULE.cooktopFromWall
            + 'mm of bench on both sides — move it in, or swap a neighbour.' })
      continue
    }
    if (short < RULE.cooktopSide) {
      out.push({ level: 'fault', code: 'cooktop-landing', runId: p.run.id, itemId: p.item.id,
        text: 'The cooktop needs ' + RULE.cooktopSide + 'mm of bench on each side to put a pan '
            + 'down; it has ' + Math.round(short) + 'mm on one. Slide it along, or move the '
            + 'tall cupboard next to it.' })
    }
    const row = ctx.placed.filter((q) => q.run === p.run).sort((a, b) => a.start - b.start)
    const i = row.indexOf(p)
    for (const n of [row[i - 1], row[i + 1]].filter(Boolean)) {
      if (n.role === 'fridge' || n.kind === 'tall' || (n.spec?.h ?? 0) > 900) {
        out.push({ level: 'advice', code: 'cooktop-tall', runId: p.run.id, itemId: p.item.id,
          text: 'A tall cupboard beside the cooktop blocks the light and the elbow room, and '
              + 'a fridge beside it works harder for its whole life. A drawer unit there is '
              + 'the usual answer.' })
        break
      }
    }
    if (p.spec?.drawers > 0) {
      out.push({ level: 'advice', code: 'cooktop-drawers', runId: p.run.id, itemId: p.item.id,
        text: 'Drawers under the cooktop are common and fine — but a stone benchtop over them '
            + 'needs a solid top under the cooktop for support. Your maker knows; this is so '
            + 'you do too.' })
    }
  }
}

function sink(ctx, out) {
  for (const p of ctx.placed.filter((q) => q.role === 'sink')) {
    const left = benchBeside(ctx, p, -1)
    const right = benchBeside(ctx, p, 1)
    const major = Math.max(left, right)
    const minor = Math.min(left, right)
    if (major < RULE.sinkSideMajor || minor < RULE.sinkSideMinor) {
      out.push({ level: 'advice', code: 'sink-landing', runId: p.run.id, itemId: p.item.id,
        text: 'A sink wants ' + RULE.sinkSideMajor + 'mm of bench on one side to stack dishes and '
            + RULE.sinkSideMinor + 'mm on the other; this one has ' + Math.round(major) + ' and '
            + Math.round(minor) + '. It will work, but it will feel tight.' })
    }
  }
}

function dishwasher(ctx, out) {
  const sinks = ctx.placed.filter((q) => q.role === 'sink')
  for (const p of ctx.placed.filter((q) => q.role === 'dishwasher')) {
    if (!sinks.length) continue
    const near = Math.min(...sinks.map((s) =>
      s.run === p.run ? Math.max(0, Math.max(s.start, p.start) - Math.min(s.end, p.end))
                      : dist(s.xy, p.xy)))
    if (near > RULE.dishwasherToSink) {
      out.push({ level: 'advice', code: 'dishwasher-far', runId: p.run.id, itemId: p.item.id,
        text: 'The dishwasher is ' + Math.round(near / 100) / 10 + 'm from the sink. Beside it '
            + 'is where the plumbing already is and where the dripping plates go.' })
    }
    if (wallBeside(ctx, p, -1) || wallBeside(ctx, p, 1)) {
      out.push({ level: 'advice', code: 'dishwasher-corner', runId: p.run.id, itemId: p.item.id,
        text: 'A dishwasher in the corner cannot be loaded from the side, and its door blocks '
            + 'the cupboard round the corner while it is open.' })
    }
  }
}

function fridge(ctx, out) {
  for (const p of ctx.placed.filter((q) => q.role === 'fridge')) {
    const landing = Math.max(benchBeside(ctx, p, -1), benchBeside(ctx, p, 1))
    if (landing < RULE.fridgeLanding) {
      out.push({ level: 'advice', code: 'fridge-landing', runId: p.run.id, itemId: p.item.id,
        text: 'There is nowhere to put the shopping down beside the fridge. '
            + RULE.fridgeLanding + 'mm of bench on the handle side is the usual minimum.' })
    }
  }
}

/**
 * The corner is a dedicated cabinet, not a place two runs happen to meet.
 *
 * Two runs that reach the same corner claim the same 560mm square of floor. What goes there
 * is a corner unit -- blind, carousel, LeMans, an L -- or a deliberate void behind a filler.
 * A plain 600 base pushed into it is a cupboard whose door hits the one round the corner and
 * whose back half nobody can reach.
 */
function corners(ctx, out) {
  const walls = ctx.plan.walls
  const base = ctx.runs.filter((r) => r.kind === 'base')
  for (const a of base) {
    // plan.walls, deliberately, not room.segment: a corner is a corner of the ROOM, and an
    // island has none. A run on an island simply has no corner rule to answer to.
    const wa = walls.find((w) => w.id === a.wallId)
    if (!wa) continue
    const wb = room.next(walls, wa)
    const b = base.find((r) => r.wallId === wb.id)
    if (!b) continue
    // Both reach the vertex they share?
    const reachA = (a.to ?? room.length(wa)) >= room.length(wa) - 0.5
    // The same number `extents` used, or the review and the plan disagree about whether the
    // second run reaches the corner at all.
    const reachB = (b.from ?? 0) <= room.cornerClaim(ctx.lim, ctx.lim.baseDepth, room.corner(wa, wb)) + 0.5
    if (!reachA || !reachB) continue
    const last = ctx.placed.filter((p) => p.run === a).sort((x, y) => y.end - x.end)[0]
    const first = ctx.placed.filter((p) => p.run === b).sort((x, y) => x.start - y.start)[0]
    const ok = [last, first].some((p) => p && (p.role === 'corner' || p.role === 'filler'))
    if (!ok && (last || first)) {
      const deg = Math.round(room.corner(wa, wb) * 180 / Math.PI)
      out.push({ level: 'advice', code: 'corner-unit', runId: a.id, itemId: last?.item.id,
        text: (deg === 90
          ? 'The corner has an ordinary cupboard in it. Makers build a corner unit there — a '
            + 'carousel, a blind corner, or an L — so the space behind is reachable and the two '
            + 'doors do not hit. Change the cabinet in the corner to "900 corner".'
          : 'This corner is ' + deg + '°, not square, so no stock corner unit fits it. A maker '
            + 'usually closes it with a filler panel and leaves the void — or builds a one-off '
            + 'angled unit, which is priced as one.') })
    }
  }
}

/** A cupboard directly above the cooktop is where the rangehood goes. */
function overheadsAboveCooktop(ctx, out) {
  for (const c of ctx.placed.filter((q) => q.role === 'cooktop')) {
    const over = ctx.placed.find((q) => q.kind === 'wall' && q.run.wallId === c.run.wallId
      && !q.item.gap && q.start < c.end && q.end > c.start)
    if (!over) continue
    if (over.role === 'rangehood') continue
    out.push({ level: 'fault', code: 'hood', runId: over.run.id, itemId: over.item.id,
      text: 'The overhead above the cooktop has to be a rangehood, not a cupboard — and it '
          + 'sits ' + RULE.hoodElectric + 'mm above an electric cooktop or ' + RULE.hoodGas
          + 'mm above gas. Swap that overhead for a rangehood.' })
  }
}

/**
 * Stone comes in slabs, and slabs have ends.
 *
 * Longer than one slab means a join, and every change of direction wants a join too, so an
 * L-shaped benchtop is never one piece. None of this is a problem; all of it is a surprise
 * if the first time anyone mentions it is the stonemason.
 */
function stone(ctx, out) {
  const bench = ctx.maker.benchtops.find((b) => b.id === ctx.plan.benchtopId)
  if (!bench || !/stone/i.test(bench.name)) return
  const base = ctx.runs.filter((r) => r.kind === 'base')
  const joins = []
  for (const r of base) {
    const len = r.to - r.from
    if (len > RULE.slab) {
      joins.push(Math.ceil(len / RULE.slab) - 1 + ' in the ' + r.wallId + ' run ('
        + Math.round(len / 1000 * 10) / 10
        + 'm is longer than one ' + RULE.slab / 1000 + 'm slab)')
    }
  }
  if (base.length > 1) joins.push('one at the corner, where the benchtop turns')
  if (joins.length) {
    out.push({ level: 'advice', code: 'stone-joins',
      text: 'This benchtop will have ' + (joins.length === 1 ? 'a join' : 'joins') + ': '
          + joins.join('; ') + '. Your maker will put each one where it shows least — '
          + 'usually beside the sink or the cooktop, never through them.' })
  }
}

/** Sink, cooktop, fridge: the walk between them. */
function triangle(ctx, out) {
  const s = ctx.placed.find((q) => q.role === 'sink')
  const c = ctx.placed.find((q) => q.role === 'cooktop')
  const f = ctx.placed.find((q) => q.role === 'fridge')
  if (!s || !c || !f) return
  const legs = [[s, c, 'sink to cooktop'], [c, f, 'cooktop to fridge'], [f, s, 'fridge to sink']]
  let total = 0
  for (const [a, b, name] of legs) {
    const d = dist(a.xy, b.xy)
    total += d
    if (d < RULE.triangleLeg[0]) {
      out.push({ level: 'advice', code: 'triangle-short',
        text: 'The ' + name + ' is only ' + Math.round(d / 100) / 10 + 'm — too close to open '
            + 'both at once.' })
    } else if (d > RULE.triangleLeg[1]) {
      out.push({ level: 'advice', code: 'triangle-long',
        text: 'The ' + name + ' is ' + Math.round(d / 100) / 10 + 'm. Over about '
            + RULE.triangleLeg[1] / 1000 + 'm every meal is a walk.' })
    }
  }
  if (total > RULE.triangleTotal) {
    out.push({ level: 'advice', code: 'triangle-total',
      text: 'Sink, cooktop and fridge are ' + Math.round(total / 100) / 10 + 'm apart in total; '
          + 'designers try to keep the three within about ' + RULE.triangleTotal / 1000 + 'm.' })
  }
}

/**
 * Doorways and windows, and the cabinets that ignore them.
 *
 * The generator steps over an opening and `extents` shortens a run for one at the end of a
 * wall, so a designed kitchen never trips this. A HAND-EDITED one does: drag a cabinet along
 * a run and it will happily sit across the doorway, because a run is a line and a line does
 * not know there is a hole in the wall behind it. This is what notices.
 *
 * Two faults and one piece of advice, and the difference between them is the sill. A door
 * blocks anything on the floor. A window on a 900 sill blocks an overhead and is FINE over a
 * bench -- it is where kitchen windows go, and calling it a fault would be wrong about most
 * kitchens in the country.
 */
function openings(ctx, out) {
  for (const o of ctx.plan.openings ?? []) {
    const span = room.spans(o)
    for (const p of ctx.placed) {
      if (p.run.wallId !== o.wallId) continue
      if (p.item.gap) continue                    // the hole itself
      if (!room.blocks(o, p.kind, ctx.lim)) continue
      if (p.end <= span.from + 1 || p.start >= span.to - 1) continue
      const what = p.spec?.name ?? 'A filler'
      out.push({
        level: 'fault',
        code: o.kind === 'door' ? 'door-blocked' : 'window-blocked',
        runId: p.run.id,
        itemId: p.item.id,
        text: o.kind === 'door'
          ? `${what} is standing across the doorway. Nothing can go in front of a door — `
            + 'move it, or take it out and let the run stop short.'
          : `${what} crosses the window. An overhead cannot go over glass; move it along, or `
            + 'use an open shelf beside the window instead.',
      })
      break     // one finding per opening: a person fixes the doorway, not each cabinet
    }

    // A window whose sill is below the benchtop, which is a real thing that happens in old
    // houses and which the maker has to know about before they template the stone.
    // The same question `blocks` asks, so the drawing and the words cannot disagree about
    // whether a window is on the bench or through it.
    const bench = ctx.lim.kickHeight + ctx.lim.baseHeight + ctx.lim.benchtopThickness
    if (o.kind === 'window' && room.blocks(o, 'base', ctx.lim)
      && ctx.runs.some((r) => r.kind === 'base' && r.wallId === o.wallId)) {
      out.push({
        level: 'advice',
        code: 'window-splash',
        text: `The window on the ${wallWord(o.wallId)} sills at ${Math.round(o.sill ?? 0)}mm, below the `
          + `${bench}mm benchtop. The bench will have to be scribed under it or stop short of it — `
          + 'your maker will want to see it before they template the stone.',
      })
    }
  }
}

const wallWord = (id) => ({ back: 'back wall', left: 'left wall', right: 'right wall',
  front: 'facing wall' }[id] ?? `${id} wall`)

/**
 * Runs that face each other need room to stand between them.
 *
 * For every pair of base runs on different walls, the shortest distance between their
 * bench fronts, if the two roughly face each other. An L's two runs never face; a galley's
 * do; a U's sides do.
 */
function walkways(ctx, out) {
  /*
   * Pairs of FACES, not pairs of runs.
   *
   * A run against a wall has one face a person can stand in front of, projecting a bench
   * front. An island has two, with different depths: the bench on the cabinet side and the
   * seating overhang on the other. Pairing runs and subtracting two bench fronts, which is
   * what this did, measured an island's seating side as if it were a bench and missed the
   * gap behind it entirely -- which is the gap somebody actually walks through.
   */
  const all = room.faces(ctx.plan, ctx.lim)
  for (let i = 0; i < all.length; i++) {
    for (let j = i + 1; j < all.length; j++) {
      const A = all[i], B = all[j]
      if (A.run === B.run) continue                    // the two sides of one island
      const na = A.n, nb = B.n
      if (na.x * nb.x + na.y * nb.y > -0.7) continue   // not facing
      const ma = A.p, mb = B.p
      // distance between the two faces along a's own normal, less what each projects
      const gap = Math.abs((mb.x - ma.x) * na.x + (mb.y - ma.y) * na.y) - A.front - B.front
      if (gap < RULE.walkway) {
        out.push({ level: 'fault', code: 'walkway',
          text: 'Only ' + Math.round(gap) + 'mm between the two runs. One person needs '
              + RULE.walkway + 'mm to stand and open a door; two need ' + RULE.walkwayTwo + '.' })
      } else if (gap < RULE.walkwayTwo) {
        out.push({ level: 'advice', code: 'walkway-two',
          text: Math.round(gap) + 'mm between the runs is fine for one cook. Two people pass '
              + 'each other comfortably at ' + RULE.walkwayTwo + '.' })
      }
    }
  }
}
